import { FunctionComponent } from 'react'
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { IReductionDisclosures } from '../../../shared/services/generated-report/generated-report.model';

const headingSx = { mb: 1, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }
const bodySx = { mb: 1, color: 'common.black' }

const formatTco2e = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : Number(value).toFixed(2)

const offsetTypeLabels: Record<string, string> = { removal: 'Removal', avoidance: 'Avoidance' }
const retirementStatusLabels: Record<string, string> = { retired: 'Retired', pending: 'Pending' }

/**
 * Supplementary reduction disclosures, shared by the GHG report view and the SECR annex.
 * Presentation rule: the report's scope totals stay gross; offsets appear only as this separate
 * register and the net figure is an explicitly labelled informational memo.
 * Sections render only when their data exists (the BE sends null per absent part).
 */
export const ReportingReductionDisclosures: FunctionComponent<{
  disclosures: IReductionDisclosures
}> = ({ disclosures }) => {
  const { targets, planCoverage, offsets, scopeTwoMethod } = disclosures
  const settings = targets?.settings
  const metrics = targets?.metrics

  return (
    <>
      {(targets || planCoverage) && (
        <Box sx={{ mt: 4, breakInside: 'avoid' }}>
          <Typography variant="subtitle1" sx={headingSx}>
            Reduction targets and progress
          </Typography>
          {settings && (
            <Typography variant="body2" sx={bodySx}>
              Reduction target: base year {settings.baseYear}, target year {settings.targetYear}
              {settings.calculationMethod === '1.5c' ? ', aligned to a 1.5°C pathway' : ', custom pathway'}.
              {settings.annualGrowthPercentage != null && settings.annualGrowthPercentage !== 0 && (
                <> Projections assume {settings.annualGrowthPercentage}% annual business growth.</>
              )}
            </Typography>
          )}
          {metrics && metrics.latestActualValue != null && metrics.finalTargetValue != null && (
            <Typography variant="body2" sx={bodySx}>
              Latest reported total ({scopeTwoMethod}): {formatTco2e(metrics.latestActualValue)} tCO<sub>2</sub>e
              {metrics.latestActualYear ? ` (${metrics.latestActualYear})` : ''}, against a final target of{' '}
              {formatTco2e(metrics.finalTargetValue)} tCO<sub>2</sub>e
              {metrics.finalTargetYear ? ` by ${metrics.finalTargetYear}` : ''}.
              {metrics.reductionToTarget != null && (
                <> Reduction still required: {formatTco2e(metrics.reductionToTarget)} tCO<sub>2</sub>e.</>
              )}
            </Typography>
          )}
          {metrics && metrics.plannedReductionByTarget != null && (
            <Typography variant="body2" sx={bodySx}>
              The committed reduction plan accounts for {formatTco2e(metrics.plannedReductionByTarget)} tCO<sub>2</sub>e
              of that reduction by the target year
              {metrics.gapToTarget != null && metrics.gapToTarget > 0 && (
                <>, leaving a gap of {formatTco2e(metrics.gapToTarget)} tCO<sub>2</sub>e not yet covered by planned measures</>
              )}.
            </Typography>
          )}
          {planCoverage && (
            <Typography variant="body2" sx={bodySx}>
              Reduction plan: {planCoverage.totalActions} committed {planCoverage.totalActions === 1 ? 'measure' : 'measures'}
              {' '}({planCoverage.completedActions} completed, {planCoverage.plannedActions} planned or in progress).
              {planCoverage.quantifiedActions > 0 && (
                <> {planCoverage.quantifiedActions} {planCoverage.quantifiedActions === 1 ? 'measure carries' : 'measures carry'} a
                  quantified saving, totalling {formatTco2e(planCoverage.quantifiedAnnualReductionTco2e)} tCO<sub>2</sub>e per year.</>
              )}
            </Typography>
          )}
        </Box>
      )}
      {offsets && offsets.rows.length > 0 && (
        <Box sx={{ mt: 4, breakInside: 'avoid' }}>
          <Typography variant="subtitle1" sx={headingSx}>
            Carbon offsets (supplementary)
          </Typography>
          <Typography variant="body2" sx={bodySx}>
            The emissions reported above are gross figures. Purchased carbon credits are disclosed
            here separately and are not deducted from any scope total.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Vintage</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Registry</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Standard</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Serial range</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Retirement</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>tCO<sub>2</sub>e</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Certificate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offsets.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.offsetType ? offsetTypeLabels[row.offsetType] ?? row.offsetType : '—'}</TableCell>
                    <TableCell>{row.vintage ?? '—'}</TableCell>
                    <TableCell>{row.registry ?? '—'}</TableCell>
                    <TableCell>{row.standard ?? '—'}</TableCell>
                    <TableCell>{row.serialRange ?? '—'}</TableCell>
                    <TableCell>
                      {row.retirementStatus
                        ? `${retirementStatusLabels[row.retirementStatus] ?? row.retirementStatus}${row.retirementDate ? ` (${row.retirementDate})` : ''}`
                        : row.retirementDate ?? '—'}
                    </TableCell>
                    <TableCell align="right">{formatTco2e(row.amountTCE)}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={row.hasCertificate ? 'Held' : 'Not held'} color={row.hasCertificate ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell colSpan={6}></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatTco2e(offsets.totalTco2e)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {offsets.netMemo && (
            <Typography variant="body2" sx={{ mt: 1, color: 'common.black' }}>
              Informational memo ({offsets.netMemo.basis}): gross emissions {formatTco2e(offsets.netMemo.grossTco2e)} tCO<sub>2</sub>e;
              offsets disclosed {formatTco2e(offsets.netMemo.offsetsTco2e)} tCO<sub>2</sub>e;
              net of offsets {formatTco2e(offsets.netMemo.netTco2e)} tCO<sub>2</sub>e.
              The net figure is informational only and does not restate the gross totals above.
            </Typography>
          )}
        </Box>
      )}
    </>
  )
}
